import {
  ApiException,
  ContractAnonymousViewModel,
  ContractsApi,
  ContractsFilter,
  ContractsGrouping,
  ContractState,
  ContractViewModel,
  CostViewModel,
  DriverViewModel,
  MonthlyCostViewModel,
  MonthlyDistanceViewModel,
  MonthlyRevenueViewModel,
  ServiceKind,
} from '@ail/contracts-api'
import { createApi } from '@reduxjs/toolkit/query/react'
import { USER_MANAGER } from '../../auth/userManager'
import { Constants } from '../../helpers/Constants'
import { getBaseQuery } from './apiQuery'

const client = new ContractsApi({
  baseUrl: Constants.contractEndpoint,
  getToken: async () => {
    const user = await USER_MANAGER.getUser()
    return user?.access_token ?? ''
  },
})

// prettier-ignore
export const contractsApi = createApi({
  reducerPath: 'contractsApi',
  baseQuery: getBaseQuery<ApiException>(),
  endpoints: (builder) => ({

    getContract: builder.query<ContractViewModel, string>({
      query: (contractId) => client.getContract(contractId),
    }),

    getContracts: builder.query<ContractViewModel[], { contractFilter?: ContractsFilter, owner?: string, requestId?: string, contractsLimit?: number, daysLimit?: number }>({
      query: (model) => client.getContractsByFilter(model.contractFilter, model.owner, model.requestId, model.contractsLimit, model.daysLimit),
    }),

    findContracts: builder.query<ContractAnonymousViewModel[], { chassisNumber?: string; licenseNumber?: string }>({
      query: (model) => client.findContracts(model.chassisNumber, model.licenseNumber),
    }),

    searchContracts: builder.query<ContractViewModel[],{ searchTerm?: string; activeOnly?: boolean }>({
      query: (model) => client.searchContracts(model.searchTerm, model.activeOnly),
    }),

    getContractsBySecurityKeys: builder.query<ContractViewModel[], { detailFrom?: string, detailTo?: string, securityKeys: string[], excludeRestricted?: boolean }>({
      query: (model) => client.getContractsBySecurityKeys(model.detailFrom, model.detailTo, model.excludeRestricted, model.securityKeys),
    }),

    getContractStatistics: builder.query<{ [key: string]: { [key: string]: number } },{ grouping?: ContractsGrouping, statuses?: ContractState[], expirationDays?: number}>({
      query: (model) => client.getContractStatistics(model.grouping, model.statuses, model.expirationDays),
    }),

    getCosts: builder.query<CostViewModel[], { contractId: string, kind: ServiceKind, from?: string, to?: string, excludeRestricted?: boolean }>({
      query: (model) => client.getCosts(model.contractId, model.kind, model.from, model.to, model.excludeRestricted),
    }),

    getMonthlyCosts: builder.query<MonthlyCostViewModel[], { contractId: string, from?: string, to?: string, excludeRestricted?: boolean }>({
      query: (model) => client.getMonthlyCosts(model.contractId, model.from, model.to, model.excludeRestricted),
    }),

    getMonthlyRevenues: builder.query<MonthlyRevenueViewModel[], { contractId: string, from?: string, to?: string, excludeRestricted?: boolean }>({
      query: (model) => client.getMonthlyRevenues(model.contractId, model.from, model.to, model.excludeRestricted),
    }),

    getMonthlyDistances: builder.query<MonthlyDistanceViewModel[], { contractId: string, from?: string, to?: string }>({
      query: (model) => client.getMonthlyDistances(model.contractId, model.from, model.to),
    }),

    getContractVehicleDrivers: builder.query<DriverViewModel[], { contractId: string, vehicleId: string }>({
      query: (model) => client.getContractVehicleDrivers(model.contractId, model.vehicleId),
    }),

    getContractsApiHealth: builder.query<{ status: 'Healthy' | 'Degraded' | 'Unhealthy' }, void>({
      query: () => fetch(`${Constants.contractEndpoint}/health`).then((r) => r.ok ? r.json() : { status: 'Unhealthy' }).then((r) => ({ data: r })),
    }),
  }),
})

export const {
  useGetContractQuery,
  useLazyGetContractQuery,
  useGetContractsQuery,
  useLazyGetContractsQuery,
  useLazyFindContractsQuery,
  useFindContractsQuery,
  useSearchContractsQuery,
  useLazySearchContractsQuery,
  useGetContractStatisticsQuery,
  useLazyGetContractStatisticsQuery,
  useGetContractsBySecurityKeysQuery,
  useLazyGetContractsBySecurityKeysQuery,
  useGetCostsQuery,
  useGetMonthlyCostsQuery,
  useGetMonthlyRevenuesQuery,
  useGetMonthlyDistancesQuery,
  useGetContractVehicleDriversQuery,
  useGetContractsApiHealthQuery,
} = contractsApi
